import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { TextInput } from "../../components/ui/Field";
import { Spinner } from "../../components/ui/misc";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { Space } from "../../lib/api/models";
import { qk, refreshInventory, useSpaces } from "../../lib/queries";

/** Where a topic or a client opens. */
export const placeHref = (place: Space): string =>
  place.kind === "client" ? `/clients/${place.id}` : `/topics/${place.slug}`;

/** Above this many places the dialog gets a filter field. */
const FILTER_FROM = 8;
const GROUPS = [
  ["internal", "Topics"],
  ["client", "Clients"],
] as const;

/**
 * Moves a document to another topic or client (document types spec §4.4). It lands at the top level of the
 * chosen place, at the end, and its sub-documents move with it. Templates is not offered.
 */
export function MoveToDialog({
  doc,
  open,
  onClose,
}: {
  doc: { id: string; title: string; spaceId: string };
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: spaces, isLoading, isError } = useSpaces();
  const [targetId, setTargetId] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const places = (spaces ?? []).filter((s) => s.kind !== "templates");
  const from = (spaces ?? []).find((s) => s.id === doc.spaceId);
  const f = filter.trim().toLowerCase();
  const shown = f ? places.filter((s) => s.name.toLowerCase().includes(f)) : places;
  // A place the filter hides is not chosen any more: Move never goes somewhere the list does not show.
  const target = shown.find((s) => s.id === targetId);

  const move = async () => {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      await api.documents.moveTo(doc.id, target.id);
    } catch (e) {
      setError(`The document could not be moved: ${e instanceof Error ? e.message : "unknown error"}`);
      setBusy(false);
      return;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.tree(doc.spaceId) }),
      queryClient.invalidateQueries({ queryKey: qk.tree(target.id) }),
      queryClient.invalidateQueries({ queryKey: ["doc"] }),
      queryClient.invalidateQueries({ queryKey: ["links"] }),
      queryClient.invalidateQueries({ queryKey: qk.internalDocs }),
      // The place it left may be a client: its counts, its item lists and open items showed this document.
      refreshInventory(queryClient, doc.spaceId),
      queryClient.invalidateQueries({ queryKey: qk.clients }),
    ]);
    toast(`“${doc.title}” moved to ${target.name}.`, "success", {
      label: `Open ${target.name}`,
      to: placeHref(target),
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Move “${doc.title}”`}
      description="It goes to the top level of the place you choose, at the end. Its sub-documents move with it."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!target || busy} onClick={() => void move()}>
            Move
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        {error ? (
          <p role="alert" className="rounded-md bg-danger-wash px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
        {from?.kind === "client" ? (
          <p className="rounded-md bg-orange-wash px-3 py-2 text-sm text-orange-ink">
            This document's links to {from.name}'s sites and items are removed by the move.
          </p>
        ) : null}
        {/* Not in the list of live places: archived, so it may be a client that Gorelo no longer lists. */}
        {spaces && !from ? (
          <p className="rounded-md bg-orange-wash px-3 py-2 text-sm text-orange-ink">
            This document is in an archived place. If it is in a client, its links to that client's sites and items are
            removed by the move.
          </p>
        ) : null}
        {isLoading ? <Spinner label="Loading…" /> : null}
        {isError ? <p className="text-sm text-danger">The topics and clients could not be loaded.</p> : null}
        {places.length > FILTER_FROM ? (
          <TextInput
            aria-label="Filter"
            placeholder="Filter topics and clients"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        ) : null}
        {f && shown.length === 0 ? <p className="text-sm text-ink-3">Nothing matches “{filter.trim()}”.</p> : null}
        {GROUPS.map(([kind, label]) => {
          const list = shown.filter((s) => s.kind === kind);
          if (list.length === 0) return null;
          return (
            <div key={kind} className="grid gap-1.5">
              <div className="text-xs font-semibold text-ink-3">{label}</div>
              <div role="radiogroup" aria-label={label} className="grid gap-1.5">
                {list.map((s) => {
                  const here = s.id === doc.spaceId;
                  const chosen = s.id === targetId;
                  return (
                    // biome-ignore lint/a11y/useSemanticElements: card-style radio rendered as a button
                    <button
                      key={s.id}
                      type="button"
                      role="radio"
                      aria-checked={chosen}
                      disabled={here}
                      onClick={() => setTargetId(s.id)}
                      className={clsx(
                        "flex items-center justify-between gap-3 rounded-lg border bg-surface px-3 py-2 text-left text-base",
                        chosen ? "border-green ring-1 ring-green" : "border-line",
                        here ? "text-ink-3" : "text-ink hover:border-line-strong",
                      )}
                    >
                      <span className="truncate">{s.name}</span>
                      {here ? <span className="shrink-0 text-xs">here now</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
}
